import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { BaseCatalogItem, CatalogItemConfig } from '../types/catalogItem';

interface UseCatalogItemMutationsOptions<
  TItem extends BaseCatalogItem,
  TUpdatePayload,
  TCreatePayload,
  TApprovePayload,
  TMergePayload,
> {
  config: CatalogItemConfig<TItem, TUpdatePayload, TCreatePayload, TApprovePayload, TMergePayload>;
  onUpdateSuccess?: (data: any, variables: { code: string; data: TUpdatePayload }) => void;
  onDeleteSuccess?: (data: any, code: string) => void;
  onDeleteError?: (error: Error, code: string) => void;
  onCreateSuccess?: (data: any, variables: TCreatePayload & { tempCode?: string }) => void;
  onApproveSuccess?: (data: any, variables: { code: string; data: TApprovePayload }) => void;
  onApproveError?: (error: Error, variables: { code: string; data: TApprovePayload }) => void;
  onDiscardSuccess?: (data: any, code: string) => void;
  onDiscardError?: (error: Error, code: string) => void;
  onMergeSuccess?: () => void;
}

export function useCatalogItemMutations<
  TItem extends BaseCatalogItem,
  TUpdatePayload,
  TCreatePayload,
  TApprovePayload,
  TMergePayload,
>({
  config,
  onUpdateSuccess,
  onDeleteSuccess,
  onDeleteError,
  onCreateSuccess,
  onApproveSuccess,
  onApproveError,
  onDiscardSuccess,
  onDiscardError,
  onMergeSuccess,
}: UseCatalogItemMutationsOptions<TItem, TUpdatePayload, TCreatePayload, TApprovePayload, TMergePayload>) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: async ({ code, data }: { code: string; data: TUpdatePayload }) => {
      return await config.updateItem({ code }, data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', config.queryKeyPrefix] });
      onUpdateSuccess?.(data, variables);
      enqueueSnackbar(`${config.itemName} updated successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (code: string) => {
      return await config.deleteItem({ code });
    },
    onSuccess: (data, code) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', config.queryKeyPrefix] });
      onDeleteSuccess?.(data, code);
      enqueueSnackbar(`${config.itemName} deleted successfully`, { variant: 'success' });
    },
    onError: (error: Error, code) => {
      onDeleteError?.(error, code);
      enqueueSnackbar(`Error deleting ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: TCreatePayload & { tempCode?: string }) => {
      const { tempCode, ...payload } = data;
      return await config.createItem(payload as TCreatePayload);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', config.queryKeyPrefix] });
      onCreateSuccess?.(data, variables);
      enqueueSnackbar(`${config.itemName} created successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async ({ code, data }: { code: string; data: TApprovePayload }) => {
      return await config.approveItem({ code }, data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', config.queryKeyPrefix] });
      onApproveSuccess?.(data, variables);
      enqueueSnackbar(`${config.itemName} approved successfully`, { variant: 'success' });
    },
    onError: (error: Error, variables) => {
      onApproveError?.(error, variables);
      enqueueSnackbar(`Error approving ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  const discardMutation = useMutation({
    mutationFn: async (code: string) => {
      return await config.discardItem({ code });
    },
    onSuccess: (data, code) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', config.queryKeyPrefix] });
      onDiscardSuccess?.(data, code);
      enqueueSnackbar(`${config.itemName} discarded successfully`, { variant: 'success' });
    },
    onError: (error: Error, code) => {
      onDiscardError?.(error, code);
      enqueueSnackbar(`Error discarding ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: async (data: TMergePayload) => {
      return await config.mergeItem(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', config.queryKeyPrefix] });
      onMergeSuccess?.();
      enqueueSnackbar(`${config.itemName} merged successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error merging ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  return {
    updateMutation,
    deleteMutation,
    createMutation,
    approveMutation,
    discardMutation,
    mergeMutation,
  };
}
